import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import python_docs from "../../assets/documentation.json";


export enum eTabs {
	overview = "overview",
	license = "license",
	reference = "reference",
	samples = "samples"
};

export enum eDocType {
	class,
	multivalue,
	property,
	method
}

export interface ISearchNode {
	matches: number,
	key: string,
	name: string,
	summary: string,
	docType: eDocType
}

export interface IPropertyNode {
	name: string;
	Signature: string;
	Summary: string;
	Link: string;
	ReadWrite: boolean;
	Deprecated: boolean;
}

export interface IMethodNode {
	name: string;
	Signature: string;
	Summary: string;
	Deprecated: boolean;
}

export interface IClassNode {
	Class: string;
	Properties: IPropertyNode[];
	Methods: IMethodNode[];
	Summary: string;
	Deprecated: boolean;
}

export interface ITreeNode {
	name: string;
	id: number;
	path: string;
	children: ITreeNode[];
	hasChildren?: boolean;
	docType: eDocType;
	doc: IClassNode | IPropertyNode | IMethodNode;
}


@Injectable({
	providedIn: 'root'
})
export class DocService {
	private _showSearch: boolean = false;
	private _showSamplesLink: boolean = false;
	private _nextId: number = 0;
	private _rootNode: ITreeNode;
	private _rootNodes: ITreeNode[] = [];
	private _searchResultSource: BehaviorSubject<ISearchNode> = new BehaviorSubject(null);
	private _searchResultObservable: Observable<ISearchNode>;
	private _showLoader: boolean = false;

	constructor() {
		let rootPath: string = 'ixnetwork_restpy.testplatform.testplatform.TestPlatform';
		let docNode: IClassNode = this.getDocNode(rootPath);
		this._rootNode = {
			name: docNode.Class,
			path: rootPath,
			id: this.nextId,
			hasChildren: true,
			children: undefined,
			doc: docNode,
			docType: eDocType.class
		};
		this._rootNodes.push(this._rootNode);
		this._searchResultObservable = this._searchResultSource.asObservable();
	}

	public get ShowSearch(): boolean {
		return this._showSearch;
	}
	
	public set ShowSearch(value: boolean) {
		this._showSearch = value;
	}

	public get ShowSamplesLink(): boolean {
		return this._showSamplesLink;
	}

	public set ShowSamplesLink(value: boolean) {
		this._showSamplesLink = value;
	}

	public get ShowLoader(): boolean {
		return this._showLoader;
	}
	
	public set ShowLoader(value: boolean) {
		this._showLoader = value;
	}

	public getDocNode(key: string): any {
		return python_docs[key];
	}

	get SearchResultObservable(): Observable<ISearchNode> {
		return this._searchResultObservable;
	}

	public SetSearchResult(searchResult: ISearchNode) {
		this._searchResultSource.next(searchResult);
	}

	get nextId(): number {
		return this._nextId++;
	}

	public get License(): string {
		return python_docs['LICENSE'];
	}

	public get Version(): string {
		return python_docs['VERSION'];
	}

	public get rootNodes(): ITreeNode[] {
		return this._rootNodes;
	}

	public getDocNodeIcon(docType: eDocType): string {
		switch (docType) {
			case eDocType.class:
				return "assets/class.png";
			case eDocType.property:
				return "assets/property.png";
			case eDocType.method:
				return "assets/method.png";
			case eDocType.multivalue:
				return "assets/multivalue.png";
		}
	}

	getClassProperties(node: any, children: any[]) {
		for (const property of node.data.doc.Properties) {
			if (property.Link === undefined) {
				continue;
			}
            let doc = this.getDocNode(property.Link);
			// if (property.Link.indexOf('.Multivalue') !== -1) {
			// 	doc.Summary = `The ${property.Signature} property returns an instance of the Multivalue class which allows the ${property.Signature} property to be set to one of it's supported ${property.Signature}.AvailablePatterns.\n\n\tReturns:\n\t\tobj(ixnetwork_restpy.multivalue.Multivalue)\n\n`;
			doc = Object.assign({}, doc);
            doc.Summary = doc.Summary + '\n\n' + property.Summary;
			// }
			children.push(
				{
					name: property.Signature,
					id: this.nextId,
					hasChildren: this.hasChildren(doc),
					doc: doc,
					docType: property.Link.indexOf('.Multivalue') !== -1 ? eDocType.multivalue : eDocType.class
				}
			)
		}
	}

	getProperties(node: any, children: any[]) {
		for (const property of node.data.doc.Properties) {
			if (property.Link) {
				continue;
			}
			let doc = property;
			children.push(
				{
					name: property.Signature,
					id: this.nextId,
					hasChildren: false,
					doc: doc,
					docType: eDocType.property
				}
			)
		}

	}

	getMethods(node: any, children: any[]) {
		for (const method of node.data.doc.Methods) {
			if (method.Signature === 'next()') {
				continue;
			}
			children.push(
				{
					name: method.Signature.slice(0, method.Signature.indexOf('(')),
					id: this.nextId,
					hasChildren: false,
					doc: method,
					docType: eDocType.method
				}
			)
		}
	}

	getChildren(node: any): Promise<any[]> {
		return new Promise<any[]>((resolve, reject) => {
			setTimeout(() => {
				let children: any[] = [];
				if (node && node.data && node.data.doc) {
					this.getClassProperties(node, children);
					this.getProperties(node, children);
					this.getMethods(node, children);
				}
				resolve(children);
			}, 0);
		});
	}

	hasChildren(doc: IClassNode): boolean {
		return doc && (doc.Properties.length > 0 || doc.Methods.length > 0)
	}
}